import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Star, ShoppingCart, Search, Filter, X, Zap } from 'lucide-react';
import api from '../utils/api';
import { Product } from '../types';
import { useCart } from '../context/CartContext';
import PriceDisplay from '../components/PriceDisplay';
import ProductImage from '../components/ProductImage';
import { MetaPixelService } from '../services/metaPixel';

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    search: '',
    minPrice: '',
    maxPrice: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [categories, setCategories] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [filters, currentPage]);

  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: '12',
        ...filters
      });

      const response = await api.get(`/products?${queryParams.toString()}`);
      setProducts(response.data.products);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/products/categories/all');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleAddToCart = async (productId: string) => {
    try {
      await addToCart(productId, 1);
      // You could add a toast notification here
      console.log('Product added to cart');
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const handleBuyNow = (product: Product) => {
    // Create a checkout item for this single product
    const buyNowItem = {
      product: product,
      quantity: 1,
      size: product.details?.sizes?.[0] || '',
      color: product.details?.colors?.[0] || '',
      price: product.discountedPrice || product.price
    };
    
    // Store in sessionStorage to pass to checkout
    sessionStorage.setItem('buyNowItem', JSON.stringify(buyNowItem));
    
    // Navigate to checkout with buy now flag
    navigate('/checkout?buyNow=true');
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
    
    // Track search events
    if (key === 'search' && value.trim()) {
      MetaPixelService.trackSearch(value.trim());
    }
  };

  if (isLoading && products.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 bg-white dark:bg-secondary-900 min-h-screen transition-colors">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-secondary-900 transition-colors">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Products
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Discover our amazing collection
            </p>
          </div>
        </div>

        {/* Mobile-First Compact Filter/Sort Bar - Hidden on Desktop */}
        <div className="mb-4 sm:mb-6 lg:hidden">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search Bar */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search products..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 w-full px-3 py-2 text-sm border border-gray-300 dark:border-secondary-600 
                         bg-white dark:bg-secondary-800 text-gray-900 dark:text-gray-100
                         rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 
                         placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
              />
            </div>

            {/* Quick Sort Dropdown */}
            <div className="flex gap-2">
              <select
                value={`${filters.sortBy}-${filters.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split('-');
                  setFilters(prev => ({ ...prev, sortBy, sortOrder }));
                }}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-secondary-600 
                         bg-white dark:bg-secondary-800 text-gray-900 dark:text-gray-100
                         rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors min-w-[120px]"
              >
                <option value="createdAt-desc">Newest</option>
                <option value="price-asc">Price ↑</option>
                <option value="price-desc">Price ↓</option>
                <option value="name-asc">A to Z</option>
              </select>

              {/* More Filters Button */}
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center text-sm font-medium min-w-[80px] justify-center"
              >
                <Filter className="h-4 w-4 mr-1" />
                Filters
              </button>
            </div>
          </div>

          {/* Collapsible Advanced Filters */}
          {showMobileFilters && (
            <div className="mt-3 p-4 bg-white dark:bg-secondary-800 border border-gray-200 dark:border-secondary-700 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">More Filters</h3>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-secondary-700 rounded"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Category */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category
                  </label>
                  <select
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-secondary-600 
                             bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                             rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                  >
                    <option value="">All Categories</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                {/* Price Range */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Price Range
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      placeholder="Min ₹"
                      value={filters.minPrice}
                      onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                      className="w-full px-2 py-2 text-sm border border-gray-300 dark:border-secondary-600 
                               bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                               rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 
                               placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                    />
                    <input
                      type="number"
                      placeholder="Max ₹"
                      value={filters.maxPrice}
                      onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                      className="w-full px-2 py-2 text-sm border border-gray-300 dark:border-secondary-600 
                               bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                               rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 
                               placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Clear Filters */}
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-secondary-600">
                <button
                  onClick={() => {
                    setFilters({
                      category: '',
                      search: '',
                      minPrice: '',
                      maxPrice: '',
                      sortBy: 'createdAt',
                      sortOrder: 'desc'
                    });
                    setShowMobileFilters(false);
                  }}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                >
                  Clear all filters
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Desktop Filters Sidebar - Hidden on mobile */}
          <div className="hidden lg:block lg:w-64">
            <div className="bg-white dark:bg-secondary-800 p-6 rounded-lg border border-gray-200 dark:border-secondary-700 shadow-sm sticky top-4">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
                Filters
              </h3>
              
              {/* Desktop filters (same as before but cleaner) */}
              <div className="space-y-4">
                {/* Search for Desktop */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Search Products
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                               bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                               rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 
                               placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  <select
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                             bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                             rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                  >
                    <option value="">All Categories</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Price Range
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.minPrice}
                      onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                               bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                               rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 
                               placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.maxPrice}
                      onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                               bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                               rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 
                               placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sort By
                  </label>
                  <select
                    value={`${filters.sortBy}-${filters.sortOrder}`}
                    onChange={(e) => {
                      const [sortBy, sortOrder] = e.target.value.split('-');
                      setFilters(prev => ({ ...prev, sortBy, sortOrder }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                             bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                             rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                  >
                    <option value="createdAt-desc">Newest First</option>
                    <option value="createdAt-asc">Oldest First</option>
                    <option value="price-asc">Price: Low to High</option>
                    <option value="price-desc">Price: High to Low</option>
                    <option value="name-asc">Name: A to Z</option>
                    <option value="name-desc">Name: Z to A</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {products.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-secondary-800 rounded-lg border border-gray-200 dark:border-secondary-700">
                <div className="text-gray-400 dark:text-gray-500 mb-4">
                  <ShoppingCart className="h-16 w-16 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  No products found
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Try adjusting your filters or search terms.
                </p>
              </div>
            ) : (
              <>
                {/* Results count */}
                <div className="mb-4 lg:mb-6">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Showing {products.length} products
                  </p>
                </div>

                {/* Products Grid */}
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-6 mb-8">
                  {products.map((product) => (
                    <div key={product._id} className="bg-white dark:bg-secondary-800 rounded-lg shadow-md hover:shadow-lg dark:hover:shadow-xl transition-all duration-200 overflow-hidden group border border-gray-200 dark:border-secondary-700">
                      <Link to={`/products/${product._id}`} className="block">
                        <div className="relative bg-white dark:bg-gray-50 overflow-hidden p-3 flex items-center justify-center">
                          <ProductImage
                            src={product.images.length > 0 ? product.images[0] : ''}
                            alt={product.name}
                            className="w-full h-40 sm:h-48 group-hover:scale-105 transition-transform duration-200"
                          />
                        </div>
                        
                        <div className="p-3 sm:p-4">
                          <h3 className="text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100 mb-2 line-clamp-2 min-h-[2.5rem]">
                            {product.name}
                          </h3>
                          
                          {/* Rating */}
                          <div className="flex items-center mb-2 flex-wrap">
                            <div className="flex items-center">
                              {renderStars(product.ratings.average)}
                            </div>
                            <span className="ml-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                              ({product.ratings.count})
                            </span>
                          </div>

                          {/* Price and Brand */}
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-1 sm:gap-2">
                            <PriceDisplay
                              price={product.price}
                              originalPrice={product.originalPrice}
                              discountedPrice={product.discountedPrice}
                              showDiscountPercentage={true}
                            />
                            {product.brand && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {product.brand}
                              </span>
                            )}
                          </div>

                          {/* Stock Status */}
                          <div className="text-xs sm:text-sm mb-3">
                            {product.stock > 0 ? (
                              <span className="text-green-600 dark:text-green-400 font-medium">
                                In Stock ({product.stock})
                              </span>
                            ) : (
                              <span className="text-red-600 dark:text-red-400 font-medium">
                                Out of Stock
                              </span>
                            )}
                          </div>
                        </div>
                      </Link>

                      {/* Action Buttons */}
                      <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleAddToCart(product._id)}
                            disabled={product.stock === 0}
                            className="flex-1 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 
                                     text-white px-3 py-3 sm:py-2 rounded-lg sm:rounded-md text-xs sm:text-sm font-medium 
                                     disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed 
                                     flex items-center justify-center transition-all active:transform active:scale-[0.98] min-h-[44px] sm:min-h-[36px]"
                          >
                            <ShoppingCart className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                            <span className="hidden xs:inline">Add to Cart</span>
                            <span className="xs:hidden">Add</span>
                          </button>
                          <button
                            onClick={() => handleBuyNow(product)}
                            disabled={product.stock === 0}
                            className="flex-1 bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 
                                     text-white px-3 py-3 sm:py-2 rounded-lg sm:rounded-md text-xs sm:text-sm font-medium 
                                     disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed 
                                     flex items-center justify-center transition-all active:transform active:scale-[0.98] min-h-[44px] sm:min-h-[36px]"
                          >
                            <Zap className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                            <span className="hidden xs:inline">Buy Now</span>
                            <span className="xs:hidden">Buy</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center space-x-1 sm:space-x-2 mt-8">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
                               bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 
                               rounded-md hover:bg-gray-50 dark:hover:bg-secondary-700 
                               disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed 
                               transition-colors"
                    >
                      Previous
                    </button>
                    
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                            currentPage === page
                              ? 'bg-primary-600 dark:bg-primary-500 text-white'
                              : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 hover:bg-gray-50 dark:hover:bg-secondary-700'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
                               bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 
                               rounded-md hover:bg-gray-50 dark:hover:bg-secondary-700 
                               disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed 
                               transition-colors"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;